import { useMemo, useState } from 'react'
import { Container, Grid } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { ProgressBar } from '@progress/kendo-react-progressbars'
import moment from 'moment'
import { SimpleLoading, GridPure, DialogPdfViewerPure } from '../../pures'
import { SimpleCard, AssistantConsumptionGraph, ModelDistributionGraph } from '../../pures/dashboard'
import './billing.css'

function formatCompactNumber(value) {
    const num = Number(value)
    if (!Number.isFinite(num)) return 'N/A'
    if (Math.abs(num) >= 1_000_000) return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M'
    if (Math.abs(num) >= 1_000) return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K'
    return num.toString()
}

const BillingView = (props) => {
    const { isLoading, billingSummary, monthlyConsumption, billingInvoices, billingCycleModelUsage, billingCycleBotUsage } = props
    const { t, i18n } = useTranslation('clients-dashboard', { keyPrefix: 'globalBillingScreen' })

    const currency = billingSummary?.currency || 'MXN'

    const tokenUsage = billingSummary
        ? { used: billingSummary.annualTokensUsed, limit: billingSummary.annualTokensLimit }
        : null

    const budgetUsage = billingSummary
        ? { spent: billingSummary.annualCostUsed, limit: billingSummary.annualCostLimit }
        : null

    const tokenPct = tokenUsage ? Math.min((tokenUsage.used / tokenUsage.limit) * 100, 100) : 0
    const budgetPct = budgetUsage ? Math.min((budgetUsage.spent / budgetUsage.limit) * 100, 100) : 0

    const modelDistributionData = useMemo(() => (
        billingSummary?.modelDistribution?.map(model => ({ name: model.model, tokens: model.tokens })) || []
    ), [billingSummary])

    const monthlyConsumptionData = useMemo(() => {
        if (!monthlyConsumption?.months) return []
        moment.locale(i18n.language === 'en' ? 'en' : 'es')
        const tokenLimit = monthlyConsumption.monthAvailableTokens
        const costLimit = billingSummary?.currentMonth?.monthlyCostLimit
        return monthlyConsumption.months.map(m => {
            const exceedanceTokens = m.exceedance?.tokens || 0
            const exceedanceCost = m.exceedance?.cost || 0
            return {
                name: moment().month(m.month - 1).format('MMM'),
                tokens: m.tokens,
                baseTokens: m.tokens - exceedanceTokens,
                exceedanceTokens,
                cost: m.cost,
                exceedanceCost,
                tokenLimit,
                costLimit
            }
        })
    }, [monthlyConsumption, billingSummary, i18n.language])

    const formatCurrency = (value, currencyOverride, options = {}) => {
        const num = Number(value)
        if (!Number.isFinite(num)) return 'N/A'
        return num.toLocaleString(i18n.language === 'en' ? 'en-US' : 'es-MX', {
            style: 'currency',
            currency: currencyOverride || currency,
            maximumFractionDigits: 0,
            ...options
        })
    }

    const invoiceRows = useMemo(() => (
        (billingInvoices?.rows || []).map((row, index) => {
            const dateValue = moment(row.generatedDate || row.createdDate)
            return {
                id: row.id ?? index,
                year: row.periodYear,
                pdfPath: row.pdfPath,
                dateDisplay: dateValue.isValid() ? dateValue.format('DD MMM YYYY') : 'N/D',
                concept: row.concept || (row.periodYear ? t('billingHistory.annualInvoiceConcept', { year: row.periodYear }) : 'N/D'),
                amountDisplay: formatCurrency(Number(row.amount) || 0, row.currency || currency, { maximumFractionDigits: 2 }),
                statusDisplay: row.status || 'N/D'
            }
        })
    ), [billingInvoices, t, currency]) // eslint-disable-line react-hooks/exhaustive-deps

    const [pdfModalUrl, setPdfModalUrl] = useState(null)

    const handleDownloadInvoice = (dataItem) => {
        if (!dataItem.pdfPath) return
        setPdfModalUrl(dataItem.pdfPath)
    }

    const botUsageRows = useMemo(() => {
        const cycleCurrency = billingCycleBotUsage?.currency || currency
        return (billingCycleBotUsage?.bots || []).map((row, index) => ({
            id: row.botConfigId ?? index,
            chatbotName: row.chatbotName,
            percentageDisplay: Number.isFinite(row.percentage) ? `${row.percentage}%` : 'N/D',
            tokensDisplay: formatCompactNumber(row.tokens),
            amountSpentDisplay: formatCurrency(row.cost, cycleCurrency, { maximumFractionDigits: 2 })
        }))
    }, [billingCycleBotUsage, currency]) // eslint-disable-line react-hooks/exhaustive-deps

    const modelUsageRows = useMemo(() => {
        const cycleCurrency = billingCycleModelUsage?.currency || currency
        return (billingCycleModelUsage?.modelsUsed || []).map((row, index) => ({
            id: row.model ?? index,
            model: row.model,
            percentageDisplay: Number.isFinite(row.percentage) ? `${row.percentage}%` : 'N/D',
            tokensDisplay: formatCompactNumber(row.tokens),
            amountSpentDisplay: formatCurrency(row.cost, cycleCurrency, { maximumFractionDigits: 2 })
        }))
    }, [billingCycleModelUsage, currency]) // eslint-disable-line react-hooks/exhaustive-deps

    if (isLoading) {
        return (
            <div className="loading">
                <SimpleLoading />
            </div>
        )
    }

    return (
        <div className="dashboardView billingView">
            <Container>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <SimpleCard title={t('tokenUsage.title')}>
                            <p className="billingView-progressValue">
                                {formatCompactNumber(tokenUsage?.used)} / {formatCompactNumber(tokenUsage?.limit)}
                            </p>
                            <ProgressBar labelVisible={false} value={tokenPct} style={{ height: 10 }} />
                            <p className="billingView-progressLabel">{t('tokenUsage.consumedLabel', { pct: tokenPct.toFixed(0) })}</p>
                        </SimpleCard>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <SimpleCard title={t('budgetUsage.title')}>
                            <p className="billingView-progressValue">
                                {formatCurrency(budgetUsage?.spent)} / {formatCurrency(budgetUsage?.limit)}
                            </p>
                            <ProgressBar labelVisible={false} value={budgetPct} style={{ height: 10 }} />
                            <p className="billingView-progressLabel">{t('budgetUsage.consumedLabel', { pct: budgetPct.toFixed(1) })}</p>
                        </SimpleCard>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <AssistantConsumptionGraph data={monthlyConsumptionData} height={300} currency={currency} />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <ModelDistributionGraph data={{ models: modelDistributionData }} height={300} />
                    </Grid>

                    <Grid item xs={12}>
                        <SimpleCard>
                            <div className="billingView-historyHeader">
                                <p className="dashboardView-card-title">{t('botUsageHistory.title')}</p>
                            </div>
                            <GridPure
                                dataGrid={botUsageRows}
                                skip={0}
                                take={10}
                                rowsItems={[
                                    { field: 'chatbotName', title: t('botUsageHistory.columns.chatbotName'), width: 200 },
                                    { field: 'percentageDisplay', title: t('botUsageHistory.columns.percentage'), width: 120 },
                                    { field: 'tokensDisplay', title: t('botUsageHistory.columns.tokens'), width: 150 },
                                    { field: 'amountSpentDisplay', title: t('botUsageHistory.columns.amountSpent'), width: 150 }
                                ]}
                            />
                        </SimpleCard>
                    </Grid>

                    <Grid item xs={12}>
                        <SimpleCard>
                            <div className="billingView-historyHeader">
                                <p className="dashboardView-card-title">{t('modelUsageHistory.title')}</p>
                            </div>
                            <GridPure
                                dataGrid={modelUsageRows}
                                skip={0}
                                take={Math.max(modelUsageRows.length, 1)}
                                rowsItems={[
                                    { field: 'model', title: t('modelUsageHistory.columns.model'), width: 200 },
                                    { field: 'percentageDisplay', title: t('modelUsageHistory.columns.percentage'), width: 120 },
                                    { field: 'tokensDisplay', title: t('modelUsageHistory.columns.tokens'), width: 150 },
                                    { field: 'amountSpentDisplay', title: t('modelUsageHistory.columns.amountSpent'), width: 150 }
                                ]}
                            />
                        </SimpleCard>
                    </Grid>

                    <Grid item xs={12}>
                        <SimpleCard>
                            <div className="billingView-historyHeader">
                                <p className="dashboardView-card-title">{t('billingHistory.title')}</p>
                            </div>
                            <GridPure
                                dataGrid={invoiceRows}
                                skip={0}
                                take={Math.max(invoiceRows.length, 1)}
                                rowsItems={[
                                    { field: 'dateDisplay', title: t('billingHistory.columns.date'), width: 150 },
                                    { field: 'concept', title: t('billingHistory.columns.concept'), width: 300 },
                                    { field: 'amountDisplay', title: t('billingHistory.columns.amount'), width: 150 },
                                    { field: 'statusDisplay', title: t('billingHistory.columns.status'), width: 150 },
                                    {
                                        field: 'Acciones',
                                        title: t('billingHistory.columns.invoice'),
                                        width: 150,
                                        actions: true,
                                        headerClassName: 'actionHeader'
                                    }
                                ]}
                                actions={[
                                    {
                                        type: 'label',
                                        value: t('billingHistory.invoice'),
                                        onClick: handleDownloadInvoice
                                    }
                                ]}
                            />
                        </SimpleCard>
                    </Grid>
                </Grid>
            </Container>

            {pdfModalUrl && (
                <DialogPdfViewerPure
                    url={pdfModalUrl}
                    title={t('billingHistory.invoice')}
                    onClose={() => setPdfModalUrl(null)}
                />
            )}
        </div>
    )
}

export { BillingView as default }
